'use client';

import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/solid';
import { Anchor } from '@mantine/core';
import cn from 'classnames';
import { useFormik } from 'formik';
import { useCreateCustomer } from 'medusa-react';
import { useState } from 'react';

import Button from '@/components/Button';
import FormCheckbox from '@/components/FormCheckbox';
import FormInput from '@/components/FormInput';
import { Props } from '@/components/LoginForm/types';
import SocialLogin from '@/components/SocialLogin';
import { useUserAgent } from '@/contexts/UserAgentContext';
import { useAuthenticate } from '@/services/hooks/auth/user.query';
import { registrationFormValidationSchema } from '@/utils/validation';

import { useCreateMarketingUser } from './hooks/useCreateMarketingUser';
import styles from './styles.module.scss';

const initialValues = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  passwordConfirm: '',
};

const RegisterForm = ({ openToast, redirectUrl }: Props) => {
  const [isRulesChecked, setIsRulesChecked] = useState(false);
  const [isDuplicatedEmail, setIsDuplicatedEmail] = useState(false);
  const [isMarketingAgreementChecked, setIsMarketingAgreementChecked] =
    useState(false);
  const [error, setError] = useState('');
  const [passwordShown, setPasswordShown] = useState(false);
  const [confirmPasswordShown, setConfirmPasswordShown] = useState(false);
  const { mutateAsync: createCustomer, isLoading: isCreateCustomerLoading } =
    useCreateCustomer();
  const { mutateAsync: signIn } = useAuthenticate();
  const {
    mutateAsync: createMarketingUser,
    isLoading: isCreateMarketingUserLoading,
  } = useCreateMarketingUser();
  const { isMacOS, isAndroid } = useUserAgent();

  const formik = useFormik({
    initialValues,
    validationSchema: registrationFormValidationSchema,
    onSubmit: async values => {
      const { email, password, firstName, lastName } = values;

      const loginValues = {
        email: email,
        password: password,
      };

      setError('');
      setIsDuplicatedEmail(false);
      if (!isRulesChecked) {
        setError('Proszę wypełnić obowiązkowe informacje');
        return;
      }
      try {
        await createCustomer({
          first_name: firstName,
          last_name: lastName,
          email,
          password,
        });
        if (isMarketingAgreementChecked) {
          await createMarketingUser({ email, userType: 'customer' });
        }
        signIn(loginValues);
        openToast?.();
      } catch (err) {
        // @ts-ignore
        if (err.response.data.type === 'duplicate_error') {
          setIsDuplicatedEmail(true);
        }
        if (
          // @ts-ignore
          err.response.data.message ===
          'A customer with the given email already has an account. Log in instead'
        ) {
          setIsDuplicatedEmail(true);
        }
        setError('Nie udało się utworzyć konta, spróbuj ponownie później.');
      }
    },
  });

  const togglePassword = () => {
    setPasswordShown(prev => !prev);
  };

  const toggleConfirmPassword = () => {
    setConfirmPasswordShown(prev => !prev);
  };

  return (
    <div className={styles.container}>
      <form
        onSubmit={formik.handleSubmit}
        className={styles.formWrapper}
        autoComplete="off"
      >
        <div className={styles.inputs}>
          <FormInput
            label="Imię"
            type="text"
            id="firstName"
            value={formik.values.firstName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur('firstName')}
            isError={Boolean(
              formik.errors.firstName && formik.touched.firstName,
            )}
            isRequired
            errorMsg={formik.errors.firstName}
          />
          <FormInput
            label="Nazwisko"
            type="text"
            id="lastName"
            value={formik.values.lastName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur('lastName')}
            isError={Boolean(formik.errors.lastName && formik.touched.lastName)}
            isRequired
            errorMsg={formik.errors.lastName}
          />
          <FormInput
            label="Adres e-mail"
            type="text"
            id="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur('email')}
            isError={Boolean(formik.errors.email && formik.touched.email)}
            isRequired
            errorMsg={formik.errors.email}
            autoComplete="new-password"
          />
          <FormInput
            label="Hasło"
            type={passwordShown ? 'text' : 'password'}
            id="password"
            description="Twoje hasło musi składać się z minimum 8 znaków."
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur('password')}
            isError={Boolean(formik.errors.password && formik.touched.password)}
            isRequired
            errorMsg={formik.errors.password}
            autoComplete="new-password"
          >
            <Button onClick={togglePassword} isIconType>
              {!passwordShown ? (
                <EyeIcon className={styles.openEyeIcon} />
              ) : (
                <EyeSlashIcon className={styles.closedEyeIcon} />
              )}
            </Button>
          </FormInput>
          <FormInput
            label="Potwierdź hasło"
            type={confirmPasswordShown ? 'text' : 'password'}
            id="passwordConfirm"
            value={formik.values.passwordConfirm}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur('passwordConfirm')}
            isError={Boolean(
              formik.errors.passwordConfirm && formik.touched.passwordConfirm,
            )}
            isRequired
            errorMsg={formik.errors.passwordConfirm}
            autoComplete="new-password"
          >
            <Button onClick={toggleConfirmPassword} isIconType>
              {!confirmPasswordShown ? (
                <EyeIcon className={styles.openEyeIcon} />
              ) : (
                <EyeSlashIcon className={styles.closedEyeIcon} />
              )}
            </Button>
          </FormInput>
        </div>
        <div className={styles.checkboxWrapper}>
          <FormCheckbox
            label={
              <>
                Zapoznałem/-am się z{' '}
                <Anchor
                  href="/regulamin"
                  className={cn(styles.link, {
                    [styles.avenirFont]: isMacOS || isAndroid,
                  })}
                >
                  Regulaminem
                </Anchor>{' '}
                oraz{' '}
                <Anchor
                  href="/polityka-prywatnosci"
                  className={cn(styles.link, {
                    [styles.avenirFont]: isMacOS || isAndroid,
                  })}
                >
                  Polityką prywatności
                </Anchor>
              </>
            }
            isRequired
            checked={isRulesChecked}
            onChange={() => setIsRulesChecked(prev => !prev)}
          />
          <FormCheckbox
            label="Wyrażam zgodę na otrzymywanie informacji handlowych na podany przeze mnie adres poczty elektronicznej i numer telefonu, przekazywanych przez patyna."
            checked={isMarketingAgreementChecked}
            onChange={() => setIsMarketingAgreementChecked(prev => !prev)}
          />
        </div>
        <Button
          type="submit"
          disabled={
            !isRulesChecked ||
            isCreateCustomerLoading ||
            isCreateMarketingUserLoading
          }
          variant="secondary"
          isCapitalize
          isBiggerPadding
        >
          Zarejestruj się
        </Button>
        {error && (
          <span className={styles.error}>
            {isDuplicatedEmail
              ? 'Podany adres e-mail już istnieje.'
              : `${error}`}
          </span>
        )}
      </form>
      <p className={styles.border}>
        <span className={styles.borderText}>lub kontynuuj z</span>
      </p>
      <SocialLogin redirectUrl={redirectUrl} />
    </div>
  );
};

export default RegisterForm;
